import React, { useState, useEffect } from 'react';
import './Qualification.css'; // Assuming you will add styles for the Kanban layout
import axios from 'axios'; // Import axios for HTTP requests
import Swal from 'sweetalert2'; // Import SweetAlert2
import { Modal, Button } from 'react-bootstrap'; // Import Bootstrap components for the modal

const FormQualification = () => {
  const [tasks, setTasks] = useState<Record<string, string[]>>({}); // Use dynamic keys for tasks
  const [columns, setColumns] = useState<string[]>([]); // Dynamic column headers
  const [cards, setCards] = useState<string[]>([]); // Separate state for cards
  const [newTask, setNewTask] = useState('');
  const [filter, setFilter] = useState(''); // State for jabatan filter
  const [jabatanList, setJabatanList] = useState<{ id: number; jabatan: string }[] | null>(null); // Allow null as initial state
  const [loading, setLoading] = useState(false); // State for loading process
  const [showModal, setShowModal] = useState(false); // State to control modal visibility
  const [jabatanId, setJabatanId] = useState<number | null>(null); // State for jabatan_id
  const [title, setTitle] = useState(''); // State for title
  const [stepNumber, setStepNumber] = useState<number>(1); // State for step_number
  const [extraColumnTasks, setExtraColumnTasks] = useState<string[]>([]); // State for extra column tasks

  useEffect(() => {
    const fetchJabatanList = async () => {
      Swal.fire({
        title: 'Loading...',
        text: 'Fetching jabatan list, please wait.',
        allowOutsideClick: false,
        didOpen: () => {
          Swal.showLoading();
        },
      });

      try {
        const token = localStorage.getItem('authToken'); // Retrieve token from localStorage
        if (!token) {
          throw new Error('Authentication token is missing. Please log in again.');
        }

        const response = await axios.get('/master-jabatan', {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (Array.isArray(response.data)) {
          setJabatanList(response.data.map((item: { id: number; jabatan: string }) => ({
            id: item.id,
            jabatan: item.jabatan,
          })));
        } else {
          console.error('Unexpected response format:', response.data);
          setJabatanList([]);
        }
      } catch (error) {
        console.error('Error fetching jabatan list:', error);
        setJabatanList([]);
      } finally {
        Swal.close(); // Close SweetAlert2 loading
      }
    };

    fetchJabatanList();

    const fetchExtraColumnTasks = async () => {
      try {
        const token = localStorage.getItem('authToken');
        if (!token) {
          throw new Error('Authentication token is missing. Please log in again.');
        }

        const response = await axios.get('/custom-fields', {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (Array.isArray(response.data)) {
          // Map the `label` field from the response data
          const tasks = response.data.map((item: { label: string }) => item.label);
          setExtraColumnTasks(tasks);
        } else {
          console.error('Unexpected response format:', response.data);
          setExtraColumnTasks([]);
        }
      } catch (error) {
        console.error('Error fetching extra column tasks:', error);
        setExtraColumnTasks([]);
      }
    };

    fetchExtraColumnTasks();
  }, []);

  const handleDragStart = (e: React.DragEvent<HTMLDivElement>, task: string, column: string) => {
    e.dataTransfer.setData('task', task);
    e.dataTransfer.setData('fromColumn', column);
  };

  const handleDrop = async (e: React.DragEvent<HTMLDivElement>, toColumn: string) => {
    const task = e.dataTransfer.getData('task');
    const fromColumn = e.dataTransfer.getData('fromColumn');

    if (fromColumn !== toColumn) {
      setTasks((prev) => {
        const updatedFromColumn = prev[fromColumn]?.filter((t) => t !== task) || [];
        const updatedToColumn = [...(prev[toColumn] || []), task]; // Ensure toColumn is initialized as an array

        return {
          ...prev,
          [fromColumn]: updatedFromColumn,
          [toColumn]: updatedToColumn,
        };
      });

      try {
        const token = localStorage.getItem('authToken');
        if (!token) {
          throw new Error('Authentication token is missing. Please log in again.');
        }

        // Post the dropped task to the server
        const payload = { task, fromColumn, toColumn };
        const response = await axios.post('/form-qualification', payload, {
          headers: { Authorization: `Bearer ${token}` },
        });

        console.log('Task successfully updated:', response.data);
        Swal.fire({
          icon: 'success',
          title: 'Success',
          text: 'Task successfully updated!',
        });
      } catch (error) {
        console.error('Error updating task:', error);
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: 'Failed to update the task. Please try again.',
        });
      }
    }
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
  };

  const handleModalClose = () => setShowModal(false);
  const handleModalShow = () => setShowModal(true);

  const handleAddCard = async (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault(); // Prevent the default form submission behavior
    if (title.trim() && jabatanId !== null) {
      const newCard = { jabatan_id: jabatanId, title, step_number: stepNumber };
      setCards((prev) => [...prev, JSON.stringify(newCard)]); // Add new card as a stringified object
      setTitle('');
      setStepNumber(1);
      handleModalClose(); // Close the modal after adding the card

      // Optionally submit the form after adding the card
      await handleFormSubmit({ preventDefault: () => {} } as React.FormEvent);
    }
  };

  const handleFilterChange = async (e: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedFilter = e.target.value;
    setFilter(selectedFilter);

    const selectedJabatan = (jabatanList || []).find((jabatan) => jabatan.jabatan === selectedFilter);
    const jabatanId = selectedJabatan ? selectedJabatan.id : null;

    Swal.fire({
      title: 'Loading...',
      text: 'Fetching data, please wait.',
      allowOutsideClick: false,
      didOpen: () => {
        Swal.showLoading();
      },
    });

    try {
      const token = localStorage.getItem('authToken');
      if (!token) {
        throw new Error('Authentication token is missing. Please log in again.');
      }

      const response = await axios.get('/form-wizard', {
        headers: { Authorization: `Bearer ${token}` },
        params: { jabatan: jabatanId },
      });

      if (response.data && Array.isArray(response.data)) {
        // Dynamically map step_number to column names and tasks
        const newTasks: Record<string, string[]> = {};
        const newColumns: string[] = [];

        response.data.forEach((item: { step_number: number; title: string }) => {
          const columnKey = `step_${item.step_number}`;
          if (!newTasks[columnKey]) {
            newTasks[columnKey] = [];
          }
          newTasks[columnKey].push(item.title);

          // Ensure column titles are unique and ordered by step_number
          if (!newColumns.includes(item.title)) {
            newColumns[item.step_number - 1] = item.title; // Use step_number to order columns
          }
        });

        setTasks(newTasks);
        setColumns(newColumns.filter(Boolean)); // Remove any undefined or empty values
      } else {
        console.error('Unexpected response format:', response.data);
        setTasks({});
        setColumns([]);
      }
    } catch (error) {
      console.error('Error fetching data from /form-wizard:', error);
      setTasks({});
      setColumns([]);
    } finally {
      Swal.close(); // Close SweetAlert2 loading
    }
  };

  const filteredCards = cards.filter((card) => {
    const cardString = typeof card === 'string' ? card : String(card); // Ensure card is a string
    return (jabatanList || []).some((jabatan) =>
      jabatan.jabatan.toLowerCase().includes(filter.toLowerCase()) &&
      cardString.toLowerCase().includes(jabatan.jabatan.toLowerCase())
    );
  });

  const handleFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    Swal.fire({
      title: 'Submitting...',
      text: 'Please wait while we submit your data.',
      allowOutsideClick: false,
      didOpen: () => {
        Swal.showLoading();
      },
    });

    try {
      const token = localStorage.getItem('authToken');
      if (!token) {
        throw new Error('Authentication token is missing. Please log in again.');
      }

      // Submit only the last card in the array as a single object
      const lastCard = cards.length > 0 ? JSON.parse(cards[cards.length - 1]) : null;
      if (!lastCard) {
        throw new Error('No card to submit.');
      }

      const payload = lastCard; // Send the last card directly as the payload
      const response = await axios.post(
        '/form-wizard',
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      console.log('Form submitted successfully:', response.data);

      Swal.fire({
        icon: 'success',
        title: 'Success',
        text: 'Form submitted successfully!',
      });
    } catch (error) {
      console.error('Error submitting form:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Failed to submit the form. Please try again.',
      });
    }
  };

  return (
    <form onSubmit={handleFormSubmit} className="kanban-board">
      <div className="row mb-4">
        <div className="col-4">
          <div className="filter-section p-3 bg-light rounded">
            <label htmlFor="filterDropdown" className="form-label fw-bold">FILTER JABATAN</label>
            <select
              id="filterDropdown"
              value={filter}
              onChange={handleFilterChange}
              className="form-select"
            >
              <option value="">Select Jabatan</option>
              {(jabatanList || []).map((jabatan) => (
                <option key={jabatan.id} value={jabatan.jabatan}>
                  {jabatan.jabatan}
                </option>
              ))}
            </select>
          </div>
        </div>
        <div className="col-4">
          <div className="task-creation p-3 bg-light rounded">
            <label className="form-label fw-bold">ADD PAGE</label>
            <button type="button" onClick={handleModalShow} className="btn btn-primary w-100">Add Page</button>
          </div>
        </div>
      </div>
      <div className="row">
        {/* Extra static Kanban column on the left */}
        <div className="col-md-4">
          <div
            className="kanban-column"
            style={{ backgroundColor: 'hsl(200, 70%, 90%)', border: '1px solid #ccc' }} // Static column styling
            onDragOver={handleDragOver}
            onDrop={(e) => handleDrop(e, 'extra_column')} // Handle drop for the extra column
          >
            <div className="kanban-column-header">
              <h5>EXTRA COLUMN</h5> {/* Static column title */}
            </div>
            <div className="kanban-cards">
              {extraColumnTasks.map((task, index) => (
                <div
                  key={index}
                  className="kanban-card"
                  style={{ backgroundColor: 'hsl(200, 80%, 95%)', border: '1px solid #ccc' }}
                  draggable
                  onDragStart={(e) => handleDragStart(e, task, 'extra_column')} // Enable drag for extra column tasks
                >
                  {task}
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Dynamic Kanban columns on the right */}
        <div className="col-md-8">
          <div className="kanban-columns">
            {columns.map((columnName, index) => {
              const columnKey = `step_${index + 1}`;
              const columnColor = `hsl(${(index * 60) % 360}, 70%, 80%)`; // Generate a unique color for each column
              return (
                <div
                  key={columnKey}
                  className="kanban-column"
                  style={{ backgroundColor: columnColor }} // Add background color to the column
                  onDragOver={handleDragOver}
                  onDrop={(e) => handleDrop(e, columnKey)} // Handle drop for dynamic columns
                >
                  <div className="kanban-column-header">
                    <h5>{`STEP ${index + 1}: ${columnName.toUpperCase()}`}</h5> {/* Convert columnName to uppercase */}
                    <button className="btn btn-label btn-secondary">
                      <i className="ri-edit-line label-icon"></i>
                      Edit
                    </button>
                  </div>
                  <div className="kanban-cards">
                    {tasks[columnKey]?.map((task, taskIndex) => {
                      const cardColor = `hsl(${(index * 60 + taskIndex * 20) % 360}, 80%, 90%)`; // Generate a unique color for each card
                      return (
                        <div
                          key={taskIndex}
                          className="kanban-card"
                          style={{ backgroundColor: cardColor, border: '1px solid #ccc' }} // Add background and border to the card
                          draggable
                          onDragStart={(e) => handleDragStart(e, task, columnKey)} // Enable drag for dynamic column tasks
                        >
                          {task}
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
      <button type="submit" className="btn btn-success mt-3">Submit</button>

      {/* Modal for adding a new card */}
      <Modal show={showModal} onHide={handleModalClose}>
        <Modal.Header closeButton>
          <Modal.Title>Add New Page</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="mb-3">
            <label htmlFor="jabatanIdInput" className="form-label">Jabatan ID</label>
            <select
              id="jabatanIdInput"
              value={jabatanId || ''}
              onChange={(e) => setJabatanId(Number(e.target.value))}
              className="form-select"
            >
              <option value="">Select Jabatan</option>
              {(jabatanList || []).map((jabatan) => (
                <option key={jabatan.id} value={jabatan.id}>
                  {jabatan.jabatan}
                </option>
              ))}
            </select>
          </div>
          <div className="mb-3">
            <label htmlFor="titleInput" className="form-label">Title</label>
            <input
              id="titleInput"
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Enter title"
              className="form-control"
            />
          </div>
          <div className="mb-3">
            <label htmlFor="stepNumberInput" className="form-label">Step Number</label>
            <input
              id="stepNumberInput"
              type="number"
              value={stepNumber}
              onChange={(e) => setStepNumber(Number(e.target.value))}
              placeholder="Enter step number"
              className="form-control"
              min="1"
            />
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleModalClose}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleAddCard}>
            Save
          </Button>
        </Modal.Footer>
      </Modal>
    </form>
  );
};

export default FormQualification;
